import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import type { WorkoutBrief } from "../../dashboardApi";
import { formatDistance, MI_PER_KM, unitLabel, useUnitsPreference } from "../../lib/useUnitsPreference";

/**
 * Canon раздел ScreenTodaySolo section 5 — ВЧЕРА card. Summary вчерашней
 * completed workout. До v153 этого ряда не было на Today — атлет должен
 * был ходить через WeekCalendar или /workout drill, чтобы увидеть, что
 * закрыл накануне.
 *
 * Источник: WorkoutBrief из useDashboard().yesterday. Card рендерится
 * только когда workout completed (отдельная YesterdayCard в
 * DashboardPage обрабатывает unmarked-state — наша card про
 * confirmed-completion summary).
 */
export function TodayYesterdayCard({ wo }: { wo: WorkoutBrief | null }) {
  const { t } = useTranslation("dashboard");
  const { unit } = useUnitsPreference();

  if (!wo || wo.status !== "completed") return null;

  const distKm = wo.actual_distance_km ?? wo.planned_distance_km;
  const durMin = wo.actual_duration_minutes ?? wo.planned_duration_minutes;
  const distStr =
    distKm != null
      ? `${formatDistance(distKm, unit, distKm < 10 ? 1 : 0).replace(/\.0+$/, "")} ${
          unit === "mi" ? unitLabel("mi") : t("unit_km")
        }`
      : null;
  const durStr = (() => {
    if (durMin == null) return null;
    const h = Math.floor(durMin / 60);
    const m = durMin % 60;
    return h ? t("duration_hm", { h, m }) : t("duration_m", { m });
  })();
  // Третья метрика — на языке дисциплины: вело думает в км/ч, плавание
  // в темпе на 100 м, бег — в темпе на км. До этого вело показывало
  // «2:28/км» — беговой темп на велопоездке.
  const paceStr = (() => {
    if (distKm == null || durMin == null || distKm <= 0 || durMin <= 0) return null;
    const d = (wo.discipline ?? "").toLowerCase();
    const isBike = ["bike", "ride", "cycle", "cycling", "gravel", "mtb", "virtualride"].includes(d);
    const isSwim = ["swim", "swimming", "openwater", "open_water"].includes(d);
    if (isBike) {
      const distInUnit = unit === "mi" ? distKm * MI_PER_KM : distKm;
      const speed = distInUnit / (durMin / 60);
      return `${speed.toFixed(1)} ${
        unit === "mi" ? t("unit_mph", { defaultValue: "mph" }) : t("unit_kmh", { defaultValue: "км/ч" })
      }`;
    }
    if (isSwim) {
      const secPer100m = (durMin * 60) / (distKm * 10);
      const mm = Math.floor(secPer100m / 60);
      const ss = Math.round(secPer100m % 60);
      return `${mm}:${String(ss).padStart(2, "0")}${t("unit_per100m", { defaultValue: "/100м" })}`;
    }
    const secPerKm = (durMin * 60) / (unit === "mi" ? distKm * MI_PER_KM : distKm);
    const mm = Math.floor(secPerKm / 60);
    const ss = Math.round(secPerKm % 60);
    return `${mm}:${String(ss).padStart(2, "0")}/${
      unit === "mi" ? unitLabel("mi") : t("unit_km")
    }`;
  })();

  const subtitle = [distStr, durStr, paceStr].filter(Boolean).join(" · ");

  return (
    <Link
      to={`/workout/${wo.id}`}
      data-testid="today-yesterday-card"
      className="block indigo-card p-3 hover:bg-bg-elevated/40 transition-colors space-y-1.5"
    >
      <div className="flex items-center gap-2 flex-wrap">
        <div className="text-[10px] font-mono font-bold uppercase tracking-[0.22em] text-fg-muted">
          {t("yesterday_card.eyebrow", { defaultValue: "ВЧЕРА" })}
        </div>
        {wo.feel && (
          <span className="text-[10px] font-mono uppercase tracking-[0.14em] text-fg-muted num-display">
            {/* Значение приходит перечислением бэкенда (good/ok/tired/…) и
                печаталось СЫРЫМ — атлет видел «GOOD» вместо «Хорошо».
                Словарь тот же, что у FeelIcon: wellness:feel.*  */}
            {t(`feel.${wo.feel}`, { ns: "wellness", defaultValue: wo.feel })}
          </span>
        )}
        <span className="ml-auto text-[10px] uppercase tracking-wider text-fg-muted">
          {t("yesterday_card.view", { defaultValue: "Открыть →" })}
        </span>
      </div>
      <div className="text-sm font-semibold text-fg-primary truncate">
        {wo.title || wo.discipline || t("default_workout_name")}
      </div>
      {subtitle && (
        <div className="text-[11px] text-fg-secondary num-display">{subtitle}</div>
      )}
    </Link>
  );
}
